/**
 * Centralized data loader — promise-level request deduplication.
 *
 * Provides a single authority for "has this data been requested yet?"
 * so multiple components can ask for the same key without racing.
 *
 * Three-state per key:
 *   undefined  = never fetched (key absent from Map)
 *   null       = in-flight (fetch started, waiting)
 *   array      = loaded (including confirmed-empty [])
 *
 * On error: resets to undefined so the next render retries.
 * Never writes [] on error. Never marks a key loaded unless the fetch covered it.
 */
class DataLoader {
  #cache    = new Map()   // key → undefined | null | array
  #promises = new Map()   // key → Promise (the one in-flight promise per key)
  #timer    = null

  /** Read current cached value. Returns undefined if key was never set. */
  get(key) { return this.#cache.get(key) }

  /**
   * Load a single key.
   * - If already in-flight: returns the existing promise (no duplicate fetch).
   * - If already loaded: returns a resolved promise.
   * - If never fetched: calls fetchFn(), sets null sentinel, stores result.
   * @param {string} key
   * @param {() => Promise<any[]>} fetchFn — must return a Promise<array>
   */
  load(key, fetchFn) {
    if (this.#promises.has(key)) return this.#promises.get(key)
    const cur = this.#cache.get(key)
    if (cur !== undefined) return Promise.resolve(cur)   // already loaded
    this.#cache.set(key, null)                           // in-flight sentinel
    const p = fetchFn()
      .then(data => {
        this.#cache.set(key, Array.isArray(data) ? data : [])
        this.#promises.delete(key)
        this.#scheduleRender()
        return data
      })
      .catch(err => {
        this.#cache.set(key, undefined)                  // reset so retry fires on next mount
        this.#promises.delete(key)
        this.#scheduleRender()
        console.error('[DataLoader] load error for key', key, err)
      })
    this.#promises.set(key, p)
    return p
  }

  /**
   * Bulk load — one batch fetch for all unloaded ids, results distributed per-key.
   *
   * batchFetchFn MUST return a Map where every id it was given has an entry
   * (either with real data or an explicit []). Any id absent from the Map
   * is treated as a fetch failure and reset to undefined (not stamped empty).
   *
   * Already in-flight or loaded ids are skipped automatically.
   *
   * @param {string[]} ids
   * @param {(id: string) => string} keyFn — maps id → cache key
   * @param {(ids: string[]) => Promise<Map<string, any[]>>} batchFetchFn
   */
  loadBatch(ids, keyFn, batchFetchFn) {
    const toFetch = ids.filter(id => this.#cache.get(keyFn(id)) === undefined)
    if (!toFetch.length) return
    // Mark all in-flight atomically BEFORE any async work
    toFetch.forEach(id => this.#cache.set(keyFn(id), null))
    const p = batchFetchFn(toFetch)
      .then(resultMap => {
        toFetch.forEach(id => {
          const key = keyFn(id)
          if (resultMap.has(id)) {
            // Use whatever the fetch returned — [] is a genuine confirmed empty
            this.#cache.set(key, resultMap.get(id))
          } else {
            // Batch did not cover this id — treat as failure, allow retry
            this.#cache.set(key, undefined)
          }
          this.#promises.delete(key)
        })
        this.#scheduleRender()
      })
      .catch(err => {
        toFetch.forEach(id => {
          this.#cache.set(keyFn(id), undefined)           // reset all to retry
          this.#promises.delete(keyFn(id))
        })
        this.#scheduleRender()
        console.error('[DataLoader] batch load error', err)
      })
    toFetch.forEach(id => this.#promises.set(keyFn(id), p))
  }

  /**
   * Write a value directly (used to seed a key without a fetch, e.g. from an optimistic update).
   */
  set(key, value) { this.#cache.set(key, value) }

  /**
   * Reset keys by prefix string, or reset everything if no prefix.
   * In-flight promises still resolve, but find their keys cleared — writes are no-ops.
   * @param {string} [prefix] — e.g. 'sheetCl:' to clear all sheet checklist entries
   */
  reset(prefix) {
    for (const key of [...this.#cache.keys()]) {
      if (!prefix || key.startsWith(prefix)) {
        this.#cache.delete(key)
        this.#promises.delete(key)
      }
    }
  }

  /** Debounced outline-render dispatch — batches concurrent completions into one render. */
  #scheduleRender() {
    if (this.#timer) return
    this.#timer = setTimeout(() => {
      this.#timer = null
      window.dispatchEvent(new Event('outline-render'))
    }, 16)   // ~1 animation frame
  }
}

export const dataLoader = new DataLoader()
